import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "n-tooltip"
}

import { ref, watch, onMounted, onBeforeUnmount, computed, useSlots, PropType } from 'vue';
import { createApp } from 'vue';
import NTooltipContent from './tooltip-content.vue';
import { createAppWithPlugins } from '@/utils/createAppWithPlugins';

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

interface TooltipContentInstance {
  hidden: boolean;
  position: TooltipPosition;
  calculatePosition(hostPosition: DOMRect): void;
  remove(): void;
  content: any;
}


export default /*@__PURE__*/_defineComponent({
  ...{
  name: 'n-tooltip',
},
  __name: 'tooltip',
  props: {
  text: {
    type: String,
    default: '',
  },
  position: {
    type: String as PropType<TooltipPosition>,
    default: 'top',
  },
  offset: {
    type: Number,
    default: undefined,
  },
  wide: {
    type: Boolean,
    default: false,
  },
},
  setup(__props) {



// Define props.
const props = __props;

// Refs for the host element and the tooltip.
const tooltipHost = ref<HTMLDivElement | null>(null);
const tooltipElement = ref<TooltipContentInstance | null>(null);
const tooltipContentHost = ref<HTMLDivElement | null>(null);
let tooltipApp: ReturnType<typeof createApp> | null = null;

const slots = useSlots();

const hasTooltipContent = computed(() => !!props.text || !!slots.message);
const isIconMode = computed(() => !slots.default);

watch(
  [() => props.text, () => slots.message],
  () => {
    if (tooltipElement.value) {
      cleanupTooltip();
    }
    if (hasTooltipContent.value) {
      generateTooltipContentElement();
    }
  },
  { deep: true },
);

watch(
  () => props.position,
  () => {
    if (tooltipElement.value) {
      tooltipElement.value.position = props.position;
      calculateTooltipPosition();
    }
  },
);

onMounted(() => {
  if (hasTooltipContent.value) {
    generateTooltipContentElement();
  }
});

onBeforeUnmount(() => {
  if (tooltipApp) {
    tooltipApp.unmount();
    tooltipApp = null;
  }
  cleanupTooltip();
});

function handleMouseEnter() {
  if (!hasTooltipContent.value || !tooltipElement.value) return;
  calculateTooltipPosition();
  tooltipElement.value.hidden = false;
}

function handleMouseLeave() {
  if (tooltipElement.value) {
    tooltipElement.value.hidden = true;
  }
}

function calculateTooltipPosition() {
  if (!tooltipHost.value || !tooltipElement.value) return;
  const hostPosition = tooltipHost.value.getBoundingClientRect();
  tooltipElement.value.calculatePosition(hostPosition);
}

function generateTooltipContentElement() {
  tooltipContentHost.value = document.createElement('div');
  document.body.appendChild(tooltipContentHost.value);

  let offset = props.offset;
  if (offset === undefined && isIconMode.value) {
    offset = -6;
  }

  tooltipApp = createAppWithPlugins(NTooltipContent, {
    content: generateTooltipContent(),
    position: props.position,
    iconOffsetPx: offset,
    wide: props.wide,
  });

  const instance = tooltipApp.mount(tooltipContentHost.value);
  tooltipElement.value = instance as unknown as TooltipContentInstance;
}

function generateTooltipContent() {
  const messageSlot = slots.message ? slots.message() : [];
  return messageSlot.length > 0 ? messageSlot : props.text;
}

function cleanupTooltip() {
  if (tooltipElement.value && typeof tooltipElement.value.remove === 'function') {
    tooltipElement.value.remove();
    tooltipElement.value = null;
  }
  if (tooltipContentHost.value && tooltipContentHost.value.parentNode) {
    document.body.removeChild(tooltipContentHost.value);
    tooltipContentHost.value = null;
  }
}

return (_ctx: any,_cache: any) => {
  return (__props.text || _ctx.$slots.default)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", {
          class: _normalizeClass(["n-tooltip__trigger", { icon: isIconMode.value }]),
          onMouseenter: handleMouseEnter,
          onMouseleave: handleMouseLeave,
          ref_key: "tooltipHost",
          ref: tooltipHost
        }, [
          _renderSlot(_ctx.$slots, "default", {}, () => [
            _cache[0] || (_cache[0] = _createElementVNode("span", { class: "n-tooltip__icon" }, [
              _createElementVNode("svg", {
                xmlns: "http://www.w3.org/2000/svg",
                height: "24",
                viewBox: "0 0 24 24",
                width: "24"
              }, [
                _createElementVNode("path", {
                  d: "M0 0h24v24H0z",
                  fill: "none"
                }),
                _createElementVNode("path", { d: "M11 18h2v-2h-2v2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-2.21 0-4 1.79-4 4h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z" })
              ])
            ], -1))
          ])
        ], 34)
      ]))
    : _createCommentVNode("", true)
}
}

})